import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { db, auth } from '../firebase';
import AutoGrowTextarea from './AutoGrowTextarea';
import { validateMessage } from '../utils/profanityFilter';
import { MATCH_MESSAGE_MAX_CHARS, MATCH_MESSAGE_MAX_IMAGES, unreadCommentCount, firstUnreadIndex } from '../utils/matchThread';
import { formatRelativeTime, toDateSafe } from '../utils/relativeTime';
import { safeCommentPhotoUrls } from '../utils/photoUrl';
import { useCommentCooldown } from '../hooks/useCommentCooldown';
import { cooldownMessage } from '../utils/commentCooldown';

const NO_MESSAGES = [];

/**
 * Comments on a match (owner request, 2026-11-01): the two matched members
 * talk here before anyone commits to an offer, with the same look and the
 * same guard rails as the comments on an offer page (OfferPage.jsx: blue card
 * for mine, gold card for theirs, up to three photos, the profanity /
 * off-platform filter). See src/utils/matchThread.js for why a thread is one
 * document with a `messages` array rather than a collection.
 *
 * The other party is notified by functions/matchThreadNotifications.js
 * (in-app, push, email), the admin console indexes the thread through
 * functions/conversationThreads.js, and firestore.rules keeps writes to the
 * two parties of the matches/{matchKey} document.
 */

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const COMPRESSION_THRESHOLD_MB = 1.5;

const pedalImage = (pedal) =>
  pedal?.images?.top || pedal?.images?.upper || pedal?.imageUrl || pedal?.placeholderImage?.url || null;

const MatchCommentThread = ({ matchKey, currentUserId, theirUserId, myPedal, theirPedal, myUserData, theirUserData, readOnly = false }) => {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const previewsRef = useRef([]);
  // Overlapping submissions: a second tap while the first is in flight must
  // not send twice. A ref, not state, so a same-tick double tap is caught.
  const sendingRef = useRef(false);
  const markingReadRef = useRef(false);
  // Arrival scroll (owner, 2026-11-01): when the first snapshot carries
  // unread comments, land on the first one. Once per mount, so a later
  // snapshot (a reply arriving while reading) never yanks the page.
  const scrollTargetIndexRef = useRef(null);
  const scrolledRef = useRef(false);
  const firstUnreadCardRef = useRef(null);
  // Stable identity for the hook below; a fresh [] per render would re-arm it.
  const messages = useMemo(() => (Array.isArray(thread?.messages) ? thread.messages : NO_MESSAGES), [thread]);
  // Four comments per member per conversation in a rolling minute (owner
  // decision, 2026-11-01). Counts stored messages only, so a failed send
  // never counts; checked again inside send(), not just on the button.
  const cooldown = useCommentCooldown(messages, currentUserId);

  useEffect(() => {
    if (!matchKey || !currentUserId) return undefined;
    const unsub = onSnapshot(
      doc(db, 'matchThreads', matchKey),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        if (data && !scrolledRef.current && scrollTargetIndexRef.current == null) {
          const idx = firstUnreadIndex(data, currentUserId);
          scrollTargetIndexRef.current = idx >= 0 ? idx : -1;
        }
        setThread(data);
        // Messages loaded successfully for this viewer: stamp how many were
        // loaded as their read marker so the Matches badge clears. The count
        // is exactly this snapshot's length, so a comment that lands between
        // this load and the write is NOT covered (the rule also caps the
        // count at the stored length). The ref stops a second write while
        // the first is in flight; the snapshot carrying the new marker brings
        // unread back to 0. Only this view writes the marker, never the list.
        if (data && unreadCommentCount(data, currentUserId) > 0 && !markingReadRef.current) {
          markingReadRef.current = true;
          updateDoc(doc(db, 'matchThreads', matchKey), { [`lastReadCount.${currentUserId}`]: data.messages.length })
            .catch((err) => console.warn('Could not mark match comments read:', err?.message || err))
            .finally(() => { markingReadRef.current = false; });
        }
      },
      (err) => console.error('Match thread listener failed:', err)
    );
    return unsub;
  }, [matchKey, currentUserId]);

  previewsRef.current = pendingPreviews;
  useEffect(() => () => previewsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  // After the thread has rendered with its arrival target, scroll there.
  //
  // Production smoke, 2026-11-01: this never fired on the Matches -> View
  // Match path. The first snapshot both captured the target AND wrote the
  // read marker; Firestore applies that write locally at once, so a second
  // snapshot re-rendered the thread within the same frame, this effect's
  // cleanup cancelled the pending animation frame, and `scrolledRef` had
  // already been set, so nothing ever scrolled. (On localhost the old rules
  // rejected the marker write, so there was no second snapshot and it worked.)
  // Now: the frame is never cancelled by a re-render, "scrolled" is recorded
  // only when the scroll actually runs, the target element is resolved at
  // that moment, and a frame that finds no element yet simply lets the next
  // render try again. The target index itself is fixed on entry and never
  // moves, so later snapshots cannot pull the reader back down.
  useEffect(() => {
    if (scrolledRef.current || scrollTargetIndexRef.current == null || scrollTargetIndexRef.current < 0) return undefined;
    if (!firstUnreadCardRef.current) return undefined;
    requestAnimationFrame(() => {
      if (scrolledRef.current) return;
      const el = firstUnreadCardRef.current;
      if (!el) return;
      scrolledRef.current = true;
      // Land the first unread card just under the fixed navbar rather than
      // mid-screen (owner, 2026-11-01: centring stopped too high, with the
      // earlier read comments filling the view). The navbar height is the
      // live measured value every page reads, so phone, desktop and the
      // native status-bar spacer all get the right offset.
      const navbarPx = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height')) || 0;
      const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - navbarPx - 16);
      if (typeof window.scrollTo === 'function') window.scrollTo({ top, behavior: 'smooth' });
      else if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return undefined;
  }, [thread]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = MATCH_MESSAGE_MAX_IMAGES - pendingFiles.length;
    if (remaining <= 0) { setError(`Maximum ${MATCH_MESSAGE_MAX_IMAGES} images per comment.`); return; }
    const toAdd = files.slice(0, remaining);
    for (const f of toAdd) {
      if (!ALLOWED_IMAGE_TYPES.includes(f.type)) { setError('Please use JPEG, PNG, or WebP images only.'); return; }
      if (f.size > MAX_FILE_SIZE_BYTES) { setError('Each image must be under 10MB.'); return; }
    }
    const urls = toAdd.map((f) => URL.createObjectURL(f));
    setPendingFiles((p) => [...p, ...toAdd]);
    setPendingPreviews((p) => [...p, ...urls]);
    setError('');
  };

  const removePending = (index) => {
    const url = pendingPreviews[index];
    if (url) URL.revokeObjectURL(url);
    setPendingFiles((p) => p.filter((_, i) => i !== index));
    setPendingPreviews((p) => p.filter((_, i) => i !== index));
  };

  const uploadImages = async (uid) => {
    const storage = getStorage();
    const stamp = Date.now();
    const urls = [];
    for (let i = 0; i < pendingFiles.length; i++) {
      let file = pendingFiles[i];
      if (file.size / 1024 / 1024 > COMPRESSION_THRESHOLD_MB) {
        try {
          file = await imageCompression(file, { maxSizeMB: 1.5, maxWidthOrHeight: 1800, useWebWorker: false, quality: 0.85, fileType: 'image/jpeg' });
        } catch (err) {
          console.warn('Match comment image compression failed, using original:', err);
        }
      }
      const name = `${uid}_${stamp}_${i}_${Math.random().toString(36).slice(2, 9)}.jpg`;
      const storageRef = ref(storage, `match_message_photos/${matchKey}/${name}`);
      await uploadBytes(storageRef, file);
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  };

  const send = async () => {
    if (sendingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed && !pendingFiles.length) return;
    const gate = cooldown.check();
    if (!gate.allowed) {
      // Draft and photos stay exactly as they are.
      setError(cooldownMessage(gate.remainingMs));
      return;
    }
    if (trimmed) {
      const v = validateMessage(trimmed);
      if (!v.isValid) { setError(v.error); return; }
    }
    const user = auth.currentUser;
    if (!user || user.uid !== currentUserId) { setError('Please log in again to comment.'); return; }
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      const imageUrls = pendingFiles.length ? await uploadImages(user.uid) : [];
      // Plain Date, not serverTimestamp(): FieldValues cannot sit inside an
      // arrayUnion element (same constraint as offer history entries).
      const entry = { from: user.uid, message: trimmed, at: new Date() };
      if (imageUrls.length) entry.imageUrls = imageUrls;
      const threadRef = doc(db, 'matchThreads', matchKey);
      const tail = { updatedAt: serverTimestamp(), lastMessageAt: serverTimestamp(), lastSenderId: user.uid };
      if (thread) {
        await updateDoc(threadRef, { ...tail, messages: arrayUnion(entry) });
      } else {
        // First comment creates the thread. Pedal + username snapshots let
        // the notifier and the admin index work without extra reads, and let
        // each party's link be rebuilt (matchPathFor) without the match doc.
        try {
          await setDoc(threadRef, {
            matchId: matchKey,
            participants: [user.uid, theirUserId],
            pedals: {
              [user.uid]: { id: myPedal.id, make: myPedal.make || '', model: myPedal.model || '', imageUrl: pedalImage(myPedal) },
              [theirUserId]: { id: theirPedal.id, make: theirPedal.make || '', model: theirPedal.model || '', imageUrl: pedalImage(theirPedal) },
            },
            usernames: { [user.uid]: myUserData?.username || '', [theirUserId]: theirUserData?.username || '' },
            messages: [entry],
            createdAt: serverTimestamp(),
            ...tail,
          });
        } catch (createErr) {
          // The other member created it a moment ago: append instead.
          if (createErr?.code === 'permission-denied' || createErr?.code === 'already-exists') {
            await updateDoc(threadRef, { ...tail, messages: arrayUnion(entry) });
          } else {
            throw createErr;
          }
        }
      }
      pendingPreviews.forEach((u) => URL.revokeObjectURL(u));
      setPendingFiles([]);
      setPendingPreviews([]);
      setText('');
    } catch (err) {
      console.error('Match comment failed:', err);
      setError('Could not send your comment. Please try again.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const canSend = !sending && cooldown.allowed && (text.trim().length > 0 || pendingFiles.length > 0);

  // Same thread as the offer page (OfferPage.jsx Trade History): cards on the
  // page background, mine right in blue, theirs left in gold, capped at 85%
  // (80% on a phone), "<name> sent a comment" with a Comment pill and a
  // relative time, and an italic "N min later" rule between cards.
  const renderSeparator = (prev, curr) => {
    const a = toDateSafe(prev?.at);
    const b = toDateSafe(curr?.at);
    if (!a || !b) return null;
    const diffMins = Math.floor(Math.abs(b - a) / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHrs / 24);
    const label = diffDays > 0 ? `${diffDays} day${diffDays > 1 ? "s" : ""} later`
      : diffHrs > 0 ? `${diffHrs} hr${diffHrs > 1 ? "s" : ""} later`
      : diffMins > 0 ? `${diffMins} min later` : "moments later";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "6px 0" }}>
        <div style={{ flex: 1, height: "1px", backgroundColor: "#d1d5db" }} />
        <span style={{ fontSize: "0.75rem", color: "#9ca3af", whiteSpace: "nowrap", fontStyle: "italic" }}>{label}</span>
        <div style={{ flex: 1, height: "1px", backgroundColor: "#d1d5db" }} />
      </div>
    );
  };

  return (
    <div className="match-comment-thread" style={{ width: "100%", textAlign: "left" }}>
      <style>{`
        .match-comment-card { max-width: 85%; }
        @media (max-width: 768px) { .match-comment-card { max-width: 80%; } }
      `}</style>

      {messages.length > 0 && (
        <div style={{ width: "100%", marginBottom: "1rem" }}>
          <h4 className="font-semibold" style={{ marginTop: 0, marginBottom: "8px" }}>Comments</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {messages.map((m, i) => {
              const mine = m.from === currentUserId;
              const sender = mine ? myUserData : theirUserData;
              const initials = (sender?.username || "??").slice(0, 2).toUpperCase();
              return (
                <Fragment key={`${m.from}_${i}`}>
                  {i > 0 && renderSeparator(messages[i - 1], m)}
                  <div className="match-comment-card" ref={i === scrollTargetIndexRef.current ? firstUnreadCardRef : undefined} data-first-unread={i === scrollTargetIndexRef.current ? "true" : undefined} style={{
                    alignSelf: mine ? "flex-end" : "flex-start",
                    width: "100%",
                    boxSizing: "border-box",
                    backgroundColor: mine ? "#f0f7fc" : "#fef9ee",
                    border: mine ? "1.5px solid #5A9ECB" : "1.5px solid #d4a843",
                    borderRadius: "12px", padding: "16px", boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, flex: "1 1 auto" }}>
                        <div style={{ width: "28px", height: "28px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, backgroundColor: mine ? "#dbeafe" : "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: "700", color: mine ? "#1d4ed8" : "#92400e" }}>
                          {sender?.photoURL ? <img src={sender.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
                        </div>
                        <span style={{ fontSize: "0.85rem", color: "#374151", minWidth: 0, overflowWrap: "anywhere", lineHeight: 1.25 }}>
                          <strong>{sender?.username || "Member"}</strong> sent a comment
                        </span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, marginLeft: "8px", gap: "2px" }}>
                        <span style={{ fontSize: "0.75rem", backgroundColor: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: "9999px", fontWeight: "500", whiteSpace: "nowrap" }}>Comment</span>
                        <span style={{ fontSize: "0.75rem", color: "#9ca3af", whiteSpace: "nowrap" }}>{formatRelativeTime(m.at)}</span>
                      </div>
                    </div>
                    {m.message && (
                      <p style={{ fontSize: "0.9rem", color: "#374151", margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{m.message}</p>
                    )}
                    {safeCommentPhotoUrls(m.imageUrls).length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: m.message ? "10px" : 0 }}>
                        {safeCommentPhotoUrls(m.imageUrls).map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ display: "block", lineHeight: 0 }}>
                            <img src={url} alt={`Attachment ${idx + 1}`} style={{ maxWidth: "180px", maxHeight: "180px", width: "auto", height: "auto", borderRadius: "8px", objectFit: "cover", border: "1px solid #e5e7eb" }} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Read-only when the match behind this thread is gone (cleanupMatches
          removed it after a want or listing changed): the words stay
          readable, nobody can add to a conversation that cannot lead to an
          offer any more, and the notifier would skip it anyway. */}
      {readOnly && (
        <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: "0.5rem 0 0 0" }}>
          This match is no longer active, so comments are closed. They are kept here for reference.
        </p>
      )}
      {!readOnly && (
      <div style={{ width: "100%", marginTop: messages.length ? "1.5rem" : 0, marginBottom: "0.5rem" }}>
        <h4 className="font-semibold" style={{ marginTop: 0, marginBottom: "2px" }}>Send a comment</h4>
        <p style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: 0, marginBottom: "6px" }}>
          Ask {theirUserData?.username || "them"} a question or talk through the trade before sending an offer.
        </p>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={onPickFiles} style={{ display: "none" }} />
        {pendingPreviews.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
            {pendingPreviews.map((url, idx) => (
              <div key={url} style={{ position: "relative" }}>
                <img src={url} alt={`Preview ${idx + 1}`} style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "8px", border: "2px solid #e0e0e0" }} />
                <button type="button" onClick={() => removePending(idx)} aria-label="Remove image"
                  style={{ position: "absolute", top: "-6px", right: "-6px", width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.95)", color: "#e74c3c", fontSize: "14px", lineHeight: 1, cursor: "pointer", padding: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}>×</button>
              </div>
            ))}
          </div>
        )}
        <AutoGrowTextarea
          placeholder="Type a comment or question..."
          maxLength={MATCH_MESSAGE_MAX_CHARS}
          rows={3}
          value={text}
          onChange={(e) => { setText(e.target.value); setError(""); }}
          style={{ marginBottom: "8px", padding: "12px", borderRadius: "8px", lineHeight: "1.5", display: "block", width: "100%", border: "1px solid #e5e7eb", fontFamily: "Nunito, sans-serif", color: "#374151", background: "#fff", boxSizing: "border-box" }}
        />
        {error && <p style={{ color: "#dc2626", fontSize: "0.85rem", margin: "0 0 8px" }}>{error}</p>}
        {!error && !cooldown.allowed && (
          <p data-testid="comment-cooldown" style={{ color: "#6b7280", fontSize: "0.85rem", margin: "0 0 8px" }}>{cooldownMessage(cooldown.remainingMs)}</p>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={sending || pendingFiles.length >= MATCH_MESSAGE_MAX_IMAGES} aria-label="Add photo"
            style={{ flexShrink: 0, width: "46px", height: "46px", borderRadius: "50%", border: "2px solid #e0e0e0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, cursor: (sending || pendingFiles.length >= MATCH_MESSAGE_MAX_IMAGES) ? "not-allowed" : "pointer", opacity: (sending || pendingFiles.length >= MATCH_MESSAGE_MAX_IMAGES) ? 0.4 : 1 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
          <button type="button" onClick={send} disabled={!canSend}
            style={{ backgroundColor: canSend ? "#C89F3C" : "#d1d5db", color: "white", border: "none", borderRadius: "25px", padding: "11px 28px", fontFamily: "Nunito, sans-serif", fontWeight: "600", fontSize: "15px", cursor: canSend ? "pointer" : "not-allowed", opacity: canSend ? 1 : 0.6 }}>
            {sending ? "Sending..." : !cooldown.allowed ? `Wait ${cooldown.remainingSeconds}s` : "Send comment"}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default MatchCommentThread;
